<template>
  <div class="date-range-picker">
    <div class="date-range-picker__fields d-inline-flex align-items-center">
      <app-input
        type="date"
        :name="fromId || undefined"
        :value="effectiveFrom"
        :min="earliest || undefined"
        :max="effectiveTo || today"
        size="sm"
        class="date-range-picker__field mb-0"
        @input="$emit('update:from', $event || null)"
      />
      <span class="date-range-picker__dash">–</span>
      <app-input
        type="date"
        :value="effectiveTo"
        :min="effectiveFrom || undefined"
        :max="today"
        size="sm"
        class="date-range-picker__field mb-0"
        @input="$emit('update:to', $event || null)"
      />
    </div>
  </div>
</template>

<script setup>
import { format } from 'date-fns';
import { computed } from 'vue';
import AppInput from '@/components/AppInput.vue';

const props = defineProps({
  from: { type: String, default: null },
  to: { type: String, default: null },
  // Earliest date this chart has data, for its active grain (from the metric
  // response). Bounds the start field; null means impose no minimum.
  earliest: { type: String, default: null },
  // Id applied to the "from" input, so an external label can associate with the
  // picker via `label-for` (a single id can't cover both inputs, so the start
  // field carries it by accessibility convention).
  fromId: { type: String, default: '' },
});

defineEmits(['update:from', 'update:to']);

const today = format(new Date(), 'yyyy-MM-dd');

// Keep the displayed range within the available data: a value that predates the
// earliest available date is shown pulled up to it. This clamps for display only
// — we deliberately don't emit here, since `earliest` arrives from the metric
// response and mutating the watched range would trigger a redundant refetch (the
// API already bounds results to the available window). User edits still emit.
const clampToEarliest = value => (props.earliest && value && value < props.earliest ? props.earliest : value);
const effectiveFrom = computed(() => clampToEarliest(props.from));
const effectiveTo = computed(() => clampToEarliest(props.to));
</script>

<style lang="scss" scoped>
.date-range-picker {
  position: relative;
}

.date-range-picker__field {
  width: 9rem;
}

.date-range-picker__dash {
  padding: 0 0.375rem;
  color: var(--palette-color-default-lighten-30);
}
</style>
